import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../LanguageSwitcher';
import AdminFilters from './AdminFilters';
import { getApiUrl, getActivationLink } from '../../config';
import config from '../../config';
import './Admin.css';

const Admin = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [registrationDateRange, setRegistrationDateRange] = useState({ start: '', end: '' });
  const [registrationSort, setRegistrationSort] = useState('none');
  const [activitySort, setActivitySort] = useState('none');
  const [editingSocial, setEditingSocial] = useState(null);
  const [socialValue, setSocialValue] = useState('');
  const [resetInfo, setResetInfo] = useState(null);

  useEffect(() => {
    fetchUsers(true);
    
    const interval = setInterval(() => {
      fetchUsers(false);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const fetchUsers = async (isInitialLoad = false) => {
    try {
      const response = await fetch(getApiUrl(config.api.endpoints.admin.users));
      const data = await response.json();
      if (data.ok) {
        setUsers(data.users);
      }
    } catch (error) {
      console.error('Ошибка загрузки пользователей:', error);
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  };

  const updateSocialNetworks = async (userId, socialNetworks) => {
    try {
      const response = await fetch(
        getApiUrl(config.api.endpoints.admin.updateSocialNetworks(userId)),
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ socialNetworks }),
        }
      );
      const data = await response.json();
      if (data.ok) {
        await fetchUsers();
        setEditingSocial(null);
      }
    } catch (error) {
      console.error('Ошибка обновления:', error);
    }
  };

  const handleResetPassword = async (userId) => {
    if (!window.confirm(t('admin.confirmResetPassword'))) {
      return;
    }

    try {
      const response = await fetch(
        getApiUrl(config.api.endpoints.auth.resetPassword(userId)),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Ошибка при генерации ссылки восстановления пароля');
      }

      const data = await response.json();

      const user = users.find((u) => u.id === userId);
      setResetInfo({
        email: user?.email || userId,
        link: data.resetPasswordLink,
      });

      console.log('Ссылка для восстановления пароля:', data.resetPasswordLink);
    } catch (error) {
      console.error('Ошибка при восстановлении пароля:', error);
      alert(t('admin.resetPasswordError'));
    }
  };

  const normalizeDate = (value) => {
    if (!value) return '';
    try {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    } catch (e) {
      return '';
    }
  };

  const getDateRangeBounds = () => {
    const start = normalizeDate(registrationDateRange.start);
    const end = normalizeDate(registrationDateRange.end);

    if (!start && !end) return null;

    if (start && !end) {
      return { start, end: start };
    }

    if (!start && end) {
      return { start: end, end };
    }

    if (start && end) {
      if (start <= end) {
        return { start, end };
      }
      return { start: end, end: start };
    }

    return null;
  };

  const filteredUsers = useMemo(() => {
    const lowerSearch = searchTerm.toLowerCase().trim();
    const range = getDateRangeBounds();

    let result = users.filter((user) => {
      const matchesSearch =
        !lowerSearch ||
        user.email.toLowerCase().includes(lowerSearch) ||
        (user.socialNetworks && user.socialNetworks.toLowerCase().includes(lowerSearch));

      if (!matchesSearch) return false;
      if (!range) return true;

      const registeredDate = normalizeDate(user.createdAt);
      if (!registeredDate) return false;

      if (registeredDate < range.start) return false;
      if (registeredDate > range.end) return false;
      return true;
    });

    if (registrationSort !== 'none') {
      result = [...result].sort((a, b) => {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        if (registrationSort === 'newest') {
          return dateB - dateA;
        }
        return dateA - dateB;
      });
    }

    if (activitySort !== 'none') {
      result = [...result].sort((a, b) => {
        const dateA = new Date(a.updatedAt || 0).getTime();
        const dateB = new Date(b.updatedAt || 0).getTime();
        if (activitySort === 'recent') {
          return dateB - dateA;
        }
        return dateA - dateB;
      });
    }

    return result;
  }, [users, searchTerm, registrationDateRange, registrationSort, activitySort]);

  const handleSortChange = (regSort, actSort) => {
    setRegistrationSort(regSort);
    setActivitySort(actSort);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getUserActivationLink = (user) => {
    if (user.isActivated) return null;
    return getActivationLink(user.activationHash);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  if (loading) {
    return (
      <div className="admin-page">
        <LanguageSwitcher />
        <div className="admin-loading">
          <div className="loading-spinner"></div>
          <p>{t('admin.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <LanguageSwitcher />
      <div className="admin-container">
        <div className="admin-header">
          <h1>{t('admin.title')}</h1>
          <div className="admin-controls">
            <AdminFilters
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              registrationDateRange={registrationDateRange}
              onDateRangeChange={setRegistrationDateRange}
              registrationSort={registrationSort}
              activitySort={activitySort}
              onSortChange={handleSortChange}
            />
            <div className="admin-stats">
              <span>{t('admin.stats.total')}: <strong>{users.length}</strong></span>
              <span>{t('admin.stats.activated')}: <strong>{users.filter(u => u.isActivated).length}</strong></span>
              <span>{t('admin.stats.pending')}: <strong>{users.filter(u => !u.isActivated).length}</strong></span>
              <span className="search-results">
                {t('admin.found')}: <strong>{filteredUsers.length}</strong> {t('admin.of')} {users.length}
              </span>
            </div>

            {resetInfo && (
              <div className="reset-link-panel">
                <div className="reset-link-header">
                  <span>{t('admin.resetPasswordFor')} {resetInfo.email}</span>
                  <button
                    type="button"
                    className="icon-btn"
                    onClick={() => setResetInfo(null)}
                    title="Close"
                  >
                    ✕
                  </button>
                </div>
                <div className="reset-link-body">
                  <input
                    type="text"
                    value={resetInfo.link}
                    readOnly
                    className="reset-link-input"
                  />
                  <button
                    type="button"
                    className="icon-btn"
                    onClick={() => copyToClipboard(resetInfo.link)}
                    title={t('admin.copy')}
                  >
                    📋
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="table-wrapper">
          <table className="users-table">
            <thead>
              <tr>
                <th>{t('admin.table.email')}</th>
                <th>{t('admin.table.registrationDate')}</th>
                <th>{t('admin.table.platform')}</th>
                <th>{t('admin.table.activationLink')}</th>
                <th>{t('admin.table.socialNetworks')}</th>
                <th>{t('admin.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => {
                const activationLink = getUserActivationLink(user);
                return (
                  <tr key={user.id}>
                    <td className="email-cell">
                      <div className="email-wrapper">
                        <span className="email-text">{user.email}</span>
                        <button
                          onClick={() => copyToClipboard(user.email)}
                          className="icon-btn"
                          title={t('admin.copy')}
                        >
                          📋
                        </button>
                      </div>
                    </td>
                    <td>{formatDate(user.createdAt)}</td>
                    <td>
                      <span className="platform-tag">{user.registrationPlatform || '-'}</span>
                    </td>
                    <td className="activation-cell">
                      {activationLink ? (
                        <div className="link-wrapper">
                          <input
                            type="text"
                            value={activationLink}
                            readOnly
                            className="link-input-table"
                          />
                          <button
                            onClick={() => copyToClipboard(activationLink)}
                            className="icon-btn"
                            title={t('admin.copy')}
                          >
                            📋
                          </button>
                        </div>
                      ) : (
                        <span className="confirmed-badge">{t('admin.user.accountConfirmed')}</span>
                      )}
                    </td>
                    <td className="social-cell">
                      {editingSocial === user.id ? (
                        <div className="social-edit-inline">
                          <textarea
                            value={socialValue}
                            onChange={(e) => setSocialValue(e.target.value)}
                            placeholder={t('admin.user.socialPlaceholder')}
                            className="social-textarea-inline"
                            rows={2}
                          />
                          <div className="inline-actions">
                            <button
                              onClick={() => updateSocialNetworks(user.id, socialValue)}
                              className="save-icon"
                              title={t('admin.save')}
                            >
                              ✓
                            </button>
                            <button
                              onClick={() => {
                                setEditingSocial(null);
                                setSocialValue('');
                              }}
                              className="cancel-icon"
                              title={t('admin.cancel')}
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="social-display-inline">
                          <span className={user.socialNetworks ? 'social-text-inline' : 'social-empty-inline'}>
                            {user.socialNetworks || t('admin.user.noSocial')}
                          </span>
                          <button
                            onClick={() => {
                              setEditingSocial(user.id);
                              setSocialValue(user.socialNetworks || '');
                            }}
                            className="edit-icon"
                            title={t('admin.edit')}
                          >
                            ✏️
                          </button>
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="actions-cell">
                        <span className={`status-indicator ${user.isActivated ? 'active' : 'pending'}`}>
                          {user.isActivated ? '✓' : '⏳'}
                        </span>
                        {user.isActivated && (
                          <button
                            className="reset-password-btn"
                            onClick={() => handleResetPassword(user.id)}
                            title={t('admin.resetPassword')}
                          >
                            🔑
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && (
          <div className="no-results">
            <p>{t('admin.noResults')}</p>
            {searchTerm && (
              <button
                className="clear-search-btn"
                onClick={() => setSearchTerm('')}
              >
                {t('admin.clearSearch')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;

